import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";
import { useAuth } from "../hooks/useAuth";
import { extractScopes, getTokenClaims } from "../utils/authStorage";
import { defaultHomePath } from "../utils/navigationScopes";

const CALENDLY_ONBOARDING_URL = "https://calendly.com/itay-datamint/30min";

type OnboardingStep = "profile" | "schedule";

type OnboardingStatusResponse = {
  name: string;
  email: string;
  onboarding_required: boolean;
};

function isPhoneFormatValid(phone: string): boolean {
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [status, setStatus] = useState<OnboardingStatusResponse | null>(null);
  const [step, setStep] = useState<OnboardingStep>("profile");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [roleLabel, setRoleLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await apiClient.get<OnboardingStatusResponse>(
          "/user/onboarding/status",
        );
        if (cancelled) return;
        setStatus(data);
        if (!data.onboarding_required) {
          const token = localStorage.getItem("token");
          if (!token) {
            navigate("/cashflow", { replace: true });
            return;
          }
          const payload = getTokenClaims(token);
          if (!payload) {
            navigate("/cashflow", { replace: true });
            return;
          }
          navigate(defaultHomePath(extractScopes(payload)), { replace: true });
        }
      } catch {
        if (!cancelled) {
          setError("Failed to load onboarding details.");
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleProfileNext = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const trimmedPhone = phone.trim();
    if (!trimmedPhone) {
      setError("Phone number is required.");
      return;
    }
    if (!isPhoneFormatValid(trimmedPhone)) {
      setError("Enter a valid phone number (7–15 digits).");
      return;
    }
    setStep("schedule");
  };

  const handleComplete = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await apiClient.post<{
        status: string;
        access_token: string;
      }>("/user/onboarding/complete", {
        phone: phone.trim(),
        company_name: companyName,
        user_role_label: roleLabel,
      });
      await login(data.access_token);
      const payload = getTokenClaims(data.access_token);
      if (!payload) {
        navigate("/cashflow", { replace: true });
        return;
      }
      navigate(defaultHomePath(extractScopes(payload)), { replace: true });
    } catch (submitError: any) {
      const message = submitError?.response?.data?.detail?.message;
      setError(message || "Failed to complete onboarding. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const stepNumber = step === "profile" ? 1 : 2;

  return (
    <div className="min-h-screen bg-linear-to-br from-emerald-50 via-teal-50 to-cyan-50 px-4 py-8">
      <div className="mx-auto max-w-xl">
        <div className="rounded-2xl border-2 border-emerald-200 bg-white p-6 shadow-xl md:p-8">
          <div className="mb-6 text-center">
            <img
              src="/logo.jpeg"
              className="mx-auto mb-4 h-16 w-44 rounded-xl object-cover"
              alt="Datamint logo"
            />
            <p className="text-xs font-medium uppercase tracking-wide text-emerald-600">
              Step {stepNumber} of 2
            </p>
            <h1 className="mt-2 text-2xl font-bold text-emerald-700">
              {step === "profile" ? "Finish your setup" : "Schedule your intro call"}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {step === "profile"
                ? "We pulled your name and email from Google. Add a few details to continue."
                : "Book a quick call with our team so we can help you get the most out of Datamint."}
            </p>
          </div>

          {step === "profile" ? (
            <form className="space-y-4" onSubmit={handleProfileNext}>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Name
                </label>
                <input
                  type="text"
                  value={status?.name ?? ""}
                  disabled
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Email
                </label>
                <input
                  type="email"
                  value={status?.email ?? ""}
                  disabled
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700"
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="mb-1 block text-sm font-medium text-slate-700"
                >
                  Phone number
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
              </div>

              <div>
                <label
                  htmlFor="company-name"
                  className="mb-1 block text-sm font-medium text-slate-700"
                >
                  Company name
                </label>
                <input
                  id="company-name"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
              </div>

              <div>
                <label
                  htmlFor="role-label"
                  className="mb-1 block text-sm font-medium text-slate-700"
                >
                  Your role
                </label>
                <input
                  id="role-label"
                  type="text"
                  value={roleLabel}
                  onChange={(e) => setRoleLabel(e.target.value)}
                  required
                  placeholder="Finance Manager"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <button
                type="submit"
                disabled={!status}
                className="w-full rounded-lg bg-emerald-600 py-2.5 font-semibold text-white transition-colors hover:bg-emerald-700 disabled:bg-emerald-300"
              >
                Next
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Pick a time that works for you. We will walk through your goals
                and how Datamint fits your workflow.
              </p>

              <a
                href={CALENDLY_ONBOARDING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full rounded-lg border-2 border-emerald-600 bg-white py-2.5 text-center font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
              >
                Open scheduling page
              </a>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <button
                type="button"
                onClick={() => void handleComplete()}
                disabled={submitting}
                className="w-full text-sm text-slate-500 underline hover:text-slate-700"
              >
                Continue without scheduling
              </button>


            </div>
          )}

          <div className="mt-4 text-center">
            <button
              onClick={logout}
              className="text-sm text-slate-400 underline hover:text-slate-600"
            >
              Sign out
            </button>
          </div>
            <button
                type="button"
                onClick={() => {
                  setError(null);
                  setStep("profile");
                }}
                disabled={submitting}
                className="w-full text-sm text-emerald-700 hover:text-emerald-900"
              >
                Back
              </button>
        </div>
      </div>
    </div>
  );
}
